import { ArrowPathIcon, CameraIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import React, { useCallback, useRef, useState } from 'react';
import api from '../api/config';
import { Avatar } from '../components/Avatar';
import Loading from '../components/Loading';
import NameModal from '../components/modals/NameModal';
import PasswordModal from '../components/modals/PasswordModal';
import PracticeDetailsModal from '../components/modals/PracticeDetailsModal';
import NotificationSettings from '../components/settings/NotificationSettings';
import PracticeSection from '../components/settings/PracticeSection';
import SecuritySettings from '../components/settings/SecuritySettings';
import SettingsCategory from '../components/settings/SettingsCategory';
import { NotFound } from '../pages/errors/NotFound';
import { useUser } from '../providers/UserProvider';
import { useToast } from '../providers/ToastProvider';
import { PracticeType, usePractice } from '../providers/practices/PracticesProvider';
import { updatePracticeProfile } from '../services/practices';
import { updateUserName } from '../services/user';
import { uploadProfilePicture } from '../services/user-files';
import {
    fetchSettings,
    updateSetting,
    getSettingValue,
    type UserSettings
} from '../services/userSettings';

const SETTINGS_QUERY_KEY = ['user-settings'];

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const compressImage = async (file: File): Promise<File> => {
    return imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        fileType: 'image/jpeg'
    });
};

const Account: React.FC = () => {
    const { user, setProfilePictureUrl, updateUser } = useUser();
    const { practices, isLoading, loadPractices } = usePractice();
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const [expandedPractices, setExpandedPractices] = useState<Set<string>>(new Set());
    const [editingPractice, setEditingPractice] = useState<PracticeType | null>(null);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isNameModalOpen, setIsNameModalOpen] = useState(false);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
        new Set(['security', 'notifications'])
    );
    const [isUploading, setIsUploading] = useState(false);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { data: settings = {} } = useQuery({
        queryKey: SETTINGS_QUERY_KEY,
        queryFn: fetchSettings,
        staleTime: 5 * 60 * 1000
    });

    const settingsMutation = useMutation({
        mutationFn: ({ category, key, value }: { category: string; key: string; value: boolean }) =>
            updateSetting(category, key, value),
        onMutate: async ({ category, key, value }) => {
            await queryClient.cancelQueries({ queryKey: SETTINGS_QUERY_KEY });
            const previous = queryClient.getQueryData<UserSettings>(SETTINGS_QUERY_KEY);

            queryClient.setQueryData<UserSettings>(SETTINGS_QUERY_KEY, (old = {}) => ({
                ...old,
                [category]: { ...old[category], [key]: JSON.stringify(value) }
            }));

            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous) {
                queryClient.setQueryData(SETTINGS_QUERY_KEY, context.previous);
            }
            showToast('Failed to update setting', 'error');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
        }
    });

    const togglePractice = (practiceId: string) => {
        setExpandedPractices((prev) => {
            const next = new Set(prev);
            if (next.has(practiceId)) {
                next.delete(practiceId);
            } else {
                next.add(practiceId);
            }
            return next;
        });
    };

    const toggleCategory = (category: string) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    const handlePhotoSelect = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;

            if (!file.type.startsWith('image/')) {
                showToast('Please select an image file', 'error');
                return;
            }
            if (file.size > MAX_FILE_SIZE) {
                showToast('Image must be smaller than 5MB', 'error');
                return;
            }

            const previewUrl = URL.createObjectURL(file);
            setPhotoPreview(previewUrl);
            setIsUploading(true);

            try {
                const compressed = await compressImage(file);
                const newUrl = await uploadProfilePicture(compressed);
                setPhotoPreview(null);
                setProfilePictureUrl(newUrl);
                showToast('Profile picture updated', 'success');
            } catch {
                showToast('Failed to upload profile picture', 'error');
                setPhotoPreview(null);
            } finally {
                setIsUploading(false);
                URL.revokeObjectURL(previewUrl);
            }
        },
        [setProfilePictureUrl, showToast]
    );

    const handleRefreshPractices = async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        try {
            await loadPractices({ refresh: true });
            showToast('Practices refreshed', 'success');
        } catch {
            showToast('Failed to refresh practices', 'error');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleToggleSetting = (category: string, key: string) => {
        if (settingsMutation.isPending) return;
        const currentValue = getSettingValue(settings, category, key);
        settingsMutation.mutate({ category, key, value: !currentValue });
    };

    const savePractice = async (data: {
        practiceId: string;
        name: string;
        phone: string;
        address?: string;
        city?: string;
        state?: string;
        zip?: string;
    }) => {
        setIsUpdateLoading(true);
        await updatePracticeProfile(data)
            .then(() => {
                loadPractices();
            })
            .catch(() => {
                setIsUpdateLoading(false);
            })
            .finally(() => {
                setEditingPractice(null);
                setIsUpdateLoading(false);
            });
    };

    const handleNameSave = useCallback(
        async (next: string) => {
            try {
                const { name } = await updateUserName(next);
                updateUser({ name });
                setIsNameModalOpen(false);
                showToast('Name updated', 'success');
            } catch (error) {
                showToast('Failed to update name', 'error');
                // Re-throw so NameModal stays open with the typed value preserved.
                throw error;
            }
        },
        [updateUser, showToast]
    );

    const handlePasswordSave = async (currentPassword: string, newPassword: string) => {
        setIsUpdateLoading(true);
        await api
            .post('/user/password', { currentPassword, newPassword })
            .then(() => {
                setIsPasswordModalOpen(false);
            })
            .catch(() => {
                setIsUpdateLoading(false);
            })
            .finally(() => {
                setIsUpdateLoading(false);
            });
    };

    if (!user) {
        return <NotFound />;
    }

    return isLoading || isUpdateLoading ? (
        <Loading />
    ) : (
        <div className="flex flex-col h-full w-full">
            <div className="absolute top-8 left-8 z-10">
                <h2 className="text-2xl font-bold text-gray-800">Account</h2>
            </div>
            <div className="flex justify-start items-center pt-40 px-8 pb-16 w-full">
                <div className="w-full bg-white rounded-xl shadow-sm p-8">
                    <div className="mb-8">
                        <div className="flex flex-col items-center gap-3 bg-gray-100 rounded-xl py-8">
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="relative group w-24 h-24 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                                disabled={isUploading}
                            >
                                <Avatar
                                    imageURL={photoPreview ?? undefined}
                                    className="w-24 h-24"
                                />
                                {isUploading ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                                        <div className="h-6 w-6 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                    </div>
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors rounded-full">
                                        <CameraIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                )}
                            </button>
                            <div className="text-center">
                                <p className="text-base font-semibold text-gray-800">
                                    {user?.name ?? 'User'}
                                </p>
                                <p className="text-sm text-gray-500">{user?.email ?? ''}</p>
                            </div>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handlePhotoSelect}
                        />
                    </div>

                    <div className="mb-8 mt-8">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex flex-col gap-1 justify-center">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-xl font-bold text-gray-800">
                                        Practice details
                                    </h3>
                                    <button
                                        onClick={handleRefreshPractices}
                                        disabled={isRefreshing}
                                        className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Refresh practices"
                                    >
                                        <ArrowPathIcon
                                            className={`w-4 h-4 text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`}
                                        />
                                    </button>
                                </div>
                                {practices.length > 0 && (
                                    <span className="text-sm text-gray-500">
                                        {practices.length} practice
                                        {practices.length !== 1 ? 's' : ''} linked to your account
                                    </span>
                                )}
                            </div>
                        </div>

                        {practices.length > 0 ? (
                            <div className="flex flex-col gap-4">
                                {practices.map((practice) => (
                                    <PracticeSection
                                        key={practice?.id ?? ''}
                                        practice={practice}
                                        isExpanded={expandedPractices.has(practice?.id ?? '')}
                                        onToggle={() => togglePractice(practice?.id ?? '')}
                                        onEdit={() => setEditingPractice(practice)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-600">
                                No practice has been assigned to this account.
                            </div>
                        )}
                    </div>

                    <hr />

                    <div className="mt-8">
                        <h3 className="text-xl font-bold text-gray-800 mb-6">Settings</h3>

                        <div className="flex flex-col gap-4">
                            <SettingsCategory
                                title="Security"
                                description="Manage your account credentials"
                                isExpanded={expandedCategories.has('security')}
                                onToggle={() => toggleCategory('security')}
                            >
                                <SecuritySettings
                                    name={user?.name ?? ''}
                                    email={user?.email ?? ''}
                                    onChangeName={() => setIsNameModalOpen(true)}
                                    onChangePassword={() => setIsPasswordModalOpen(true)}
                                />
                            </SettingsCategory>

                            <SettingsCategory
                                title="Notifications"
                                description="Manage your email notification preferences"
                                isExpanded={expandedCategories.has('notifications')}
                                onToggle={() => toggleCategory('notifications')}
                            >
                                <NotificationSettings
                                    settings={settings}
                                    onToggle={(key) => handleToggleSetting('notifications', key)}
                                />
                            </SettingsCategory>
                        </div>
                    </div>
                </div>
            </div>

            <PracticeDetailsModal
                isOpen={editingPractice !== null}
                onClose={() => setEditingPractice(null)}
                initial={{
                    practiceId: editingPractice?.id ?? '',
                    name: editingPractice?.name ?? '',
                    phone: editingPractice?.phone ?? '',
                    address: editingPractice?.address ?? '',
                    city: editingPractice?.city ?? '',
                    state: editingPractice?.state ?? '',
                    zip: editingPractice?.zip ?? ''
                }}
                onSave={savePractice}
            />
            <PasswordModal
                isOpen={isPasswordModalOpen}
                onClose={() => setIsPasswordModalOpen(false)}
                onSave={(currentPassword, newPassword) =>
                    handlePasswordSave(currentPassword, newPassword)
                }
            />
            <NameModal
                isOpen={isNameModalOpen}
                initialName={user?.name ?? ''}
                onClose={() => setIsNameModalOpen(false)}
                onSave={handleNameSave}
            />
        </div>
    );
};

export default Account;
